import { Injectable } from '@angular/core';
import Pusher, { Authorizer } from 'pusher-js';
import { firstValueFrom, interval, Observable, Subject } from 'rxjs';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { ConnectionState } from '../../models';
import { AbstractPusherConfigService } from '../abstract-pusher-config/abstract-pusher-config.service';
import { PusherApiService } from '../pusher-api/pusher-api.service';

interface CacheRef {
  pusherClient: Pusher;
}

const logsSubject = new Subject();
Pusher.log = (message) => {
  logsSubject.next(message);
};

// /**
//  * Pusher service is in charge of handling all necessary stuff for our Pusher connection.
//  * It is loaded in main app component, connects, handles authentication and then binds himself to the
//  * customer channel according to the customer ID.
//  *
//  * A component/service wishing to subscribe to notifications should inject PusherService and subscribe to
//  * notifications$ which will emit a new ServerEventMessage every time a message is pushed through the channel.
//  *
//  * ServerEventMessage is built in that way:
//  * {
//  * message_type: is of type PusherMessageType and may be one of values specified in that object (Control/Evidence/etc..)
//  * message: string (nullable) that can contain a textual representation of the message we want to deliver
//  * message_object: object that depends on the message type, for instance, a control updated will look like this:
//  *
//  * {"message_type": "CONTROL", "message": "", "message_object": {CONTROL_INSTANCE_OBJECT}}
//  *
//  * 1 exception is the CONNECTIVITY message type which will have message_object of type ConnectivityResult (refer to the
//  * models for further details on the structure)
//  * }
//  */
@Injectable()
export class PusherClientService {
  /** Might be used to mitigate pusher.
   * If it equals to 0 it won't trigger client recreation.
   */
  private static recreateClientInterval = 0;
  private clientCache: Observable<Pusher>;

  constructor(
    private pusherApi: PusherApiService,
    private config: AbstractPusherConfigService
  ) {}

  getClient(): Observable<Pusher> {
    if (!this.clientCache) {
      this.clientCache = new Observable<Pusher>((subscriber) => {
        if (PusherClientService.recreateClientInterval) {
          return interval(PusherClientService.recreateClientInterval).subscribe(() => subscriber.next());
        } else {
          subscriber.next();
        }
      }).pipe(
        map(() => ({ pusherClient: null } as CacheRef)),
        tap((cacheRef) => {
          if (cacheRef.pusherClient) {
            this.disconnectPusher(cacheRef.pusherClient);
          }
        }),
        map((cacheRef) => {
          return (cacheRef.pusherClient = this.createClient());
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    }

    return this.clientCache;
  }

  getLogs(): Observable<any> {
    return logsSubject;
  }

  getConnectionState(): Observable<ConnectionState> {
    return this.getClient().pipe(switchMap((pusherClient) => {
      return new Observable<ConnectionState>((subscriber) => {
        const eventName = 'state_change';

        function connectionStateHandler(state): void {
          subscriber.next(ConnectionState.Unavailable ||  state.current);
        }

        pusherClient.connection.bind(eventName, connectionStateHandler);

        return () => pusherClient.unbind(eventName, connectionStateHandler);
      });
    }));
  }

  private disconnectPusher(pusherClient: Pusher): void {
    pusherClient.channels.disconnect();
    pusherClient.disconnect();
  }

  private createClient(): Pusher {
    return new Pusher(this.config.getConfig().applicationId, {
      cluster: this.config.getConfig().cluster,
      authorizer: (channel) => this.authorize(channel),
      enabledTransports: ['ws', 'wss'],
    });
  }

  private authorize(channel): Authorizer {
    return {
      authorize: async (socketId, callback) => {
        try {
          const res = await firstValueFrom(this.pusherApi.authenticate(channel.name, socketId));
          if (!res?.auth) {
            throw new Error(`Received ${res}`);
          }
          return callback(null, res);
        } catch (err) {
          callback(new Error(`Error calling auth API: ${err}`), {
            auth: '',
          });
        }
      },
    };
  }
}
